import React, { useState, useEffect, useRef } from 'react';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import { ChartBarIcon, XMarkIcon } from '@heroicons/react/24/outline';
import api from '../../../../api';
import { getRoundRobinBaseUrl } from '../../../../loadBalancer';
import { useAuth } from '../../../Layouts/context/Authcontext';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, Filler);

const ScopeQuality = ({ filters }) => {
  const [focus, setFocus] = useState(false);
  const { accessToken } = useAuth();
  const chartRef = useRef(null);

  const [labels, setLabels] = useState([]);
  const [rawData, setRawData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchScopeQualityData = async () => {
      try {
        setLoading(true);
        const filteredParams = {};
        Object.keys(filters || {}).forEach((key) => {
          if (filters[key]) filteredParams[key] = filters[key];
        });
        const baseUrl = getRoundRobinBaseUrl();
        const response = await api.get(`${baseUrl}/superAdminDashboard/getSuperAdminScopeWiseQualityCount`, {
          headers: { Authorization: `Bearer ${accessToken}` },
          params: filteredParams,
        });

        if (response.data.success && response.data.data) {
          const fetchedLabels = response.data.data.currentMonthData.map((item) => item._id);
          const fetchedData = response.data.data.currentMonthData.map((item) => item.qualityPercentage);
          setLabels(fetchedLabels);
          setRawData(fetchedData);
        } else {
          console.error('Failed to fetch Scope Quality data');
        }
      } catch (error) {
        console.error('Error fetching Scope Quality data:', error);
      } finally {
        setLoading(false);
      }
    };

    if (filters) fetchScopeQualityData();
  }, [filters, accessToken]);

  const noData = rawData.length === 0 || rawData.every((val) => val === 0 || val == null);

  const chartData = {
    labels,
    datasets: [
      {
        label: 'Scope Quality %',
        data: rawData,
        fill: true,
        backgroundColor: 'rgba(26,43,92,0.1)',
        borderColor: '#1A2B5C',
        pointBackgroundColor: '#EE1D23',
        pointBorderColor: '#fff',
        pointHoverBackgroundColor: '#1A2B5C',
        tension: 0.4,
        borderWidth: 3,
        pointRadius: 5,
        pointHoverRadius: 8,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        labels: {
          color: '#374151',
          font: { size: 14, weight: '600' },
        },
      },
      tooltip: {
        backgroundColor: '#F3F4F6',
        titleColor: '#1A2B5C',
        bodyColor: '#374151',
        borderColor: '#E5E7EB',
        borderWidth: 1,
        cornerRadius: 10,
        padding: 10,
        callbacks: {
          title: (ctx) => `Scope: ${ctx[0].label}`,
          label: (ctx) => `Quality %: ${Math.round(ctx.raw)}%`, // Rounded tooltip value
        },
      },
    },
    scales: {
      x: {
        ticks: {
          color: '#4B5563',
          font: { size: 13 },
        },
        grid: { display: false },
      },
      y: {
        ticks: {
          color: '#6B7280',
          callback: (val) => `${val}%`,
        },
        grid: {
          color: '#E5E7EB',
        },
        min: 80,
        max: 100,
      },
    },
  };

  const LoadingSkeleton = () => (
    <div className="flex flex-col items-center justify-center h-full animate-pulse text-center gap-4">
      <div className="w-3/4 h-5 rounded-md bg-gray-200" />
      <div className="w-1/2 h-4 rounded-md bg-gray-200" />
      <div className="w-full h-52 rounded-xl bg-gray-100" />
    </div>
  );

  const NoDataMessage = () => (
    <div className="flex flex-col items-center justify-center h-full text-center animate-fadeIn">
      <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 text-[#EE1D23]/70 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.75V3m0 18v-1.75M6.364 6.364l-1.061-1.061m12.728 12.728l-1.061-1.061M4.75 12H3m18 0h-1.75M6.364 17.636l-1.061 1.061m12.728-12.728l-1.061 1.061" />
      </svg>
      <div className="text-lg font-semibold text-[#1A2B5C] mb-1">No Data Available</div>
      <p className="text-sm text-gray-500 max-w-sm">
        No information found for the current Scope Quality. Try refreshing or adjusting your filters.
      </p>
    </div>
  );

  const ChartCard = () => (
    <div className="relative mt-12 w-full rounded-3xl shadow-xl border border-gray-200 bg-white/80 backdrop-blur-lg overflow-hidden group transition duration-700 hover:scale-[1.01]">
      <div className="relative z-10 px-6 py-6 flex items-center justify-between">
        <div>
          <div className="flex items-center gap-3">
            <ChartBarIcon className="w-6 h-6 text-[#EE1D23]" />
            <h2 className="text-xl font-bold uppercase tracking-wider">Scope Quality</h2>
          </div>
          <div className="w-24 h-1 mt-1 rounded-full bg-gradient-to-r from-[#EE1D23] to-[#1A2B5C] animate-pulse" />
        </div>
        <button
          onClick={() => setFocus(true)}
          className="px-4 py-1.5 text-xs font-medium rounded-md shadow-md border border-[#1A2B5C] text-[#1A2B5C] hover:bg-[#1A2B5C] hover:text-white transition"
        >
          Focus Mode
        </button>
      </div>
      <div className="relative z-10 px-6 pb-8 h-[320px]">
        {loading ? <LoadingSkeleton /> : noData ? <NoDataMessage /> : (
          <Line data={chartData} options={chartOptions} ref={chartRef} />
        )}
      </div>
    </div>
  );

  const FocusMode = () => (
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="relative max-w-6xl w-full h-[90vh] p-8 rounded-3xl overflow-auto shadow-2xl bg-white border border-gray-300 text-[#1A2B5C]">
        <div className="absolute inset-0 -z-10 blur-2xl bg-gradient-to-br from-white/20 to-transparent opacity-40 rounded-3xl" />
        <button
          onClick={() => setFocus(false)}
          className="absolute top-5 right-5 bg-white shadow-md rounded-full p-2 text-gray-600 hover:text-[#EE1D23] hover:shadow-lg transition"
          title="Exit Focus Mode"
        >
          <XMarkIcon className="w-5 h-5" />
        </button>
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <ChartBarIcon className="w-6 h-6 text-[#1A2B5C]" />
            <h2 className="text-2xl font-bold tracking-wide">Scope Quality (Focus Mode)</h2>
          </div>
          <div className="h-1.5 w-32 rounded-full bg-gradient-to-r from-[#1A2B5C] to-[#3B4A6B]" />
        </div>
        <div className="h-[60vh]">
          {loading ? <LoadingSkeleton /> : noData ? <NoDataMessage /> : (
            <Line data={chartData} options={chartOptions} ref={chartRef} />
          )}
        </div>
      </div>
    </div>
  );

  return <>{focus ? <FocusMode /> : <ChartCard />}</>;
};

export default ScopeQuality; 